import React, { useState, useEffect, useMemo, useRef } from 'react';
import { ArrowLeft, ArrowUpRight, X } from 'lucide-react';
import TranscriptionTab from './protocol/TranscriptionTab';
import ProtocolTab from './protocol/ProtocolTab';
import ShareModal from './ShareModal';
import { viewsLabel } from '../lib/shareViews';
import EditSpeakersModal from './EditSpeakersModal';
import TranscriptEditor, { loadDraft } from './history/TranscriptEditor';
import FindReplaceBar from './history/FindReplaceBar';
import { extractSpeakerLabels, applySpeakerRename, looksDiarized } from './protocol/speakerLabels';
import { historyService } from '../services/historyService';
import { promptService } from '../services/promptService';
import { summaryLabel } from '../lib/summaryLabel';
import { speakerShares } from '../lib/speakerShares';
import { NO_SPEECH_MESSAGE, resultIsEmpty } from '../lib/noSpeech';
import { useIsMobile } from '../hooks/useIsMobile';

const noop = () => {};

const TYPE_TITLES = {
  transcription: 'Файл',
  dual_channel: 'АТС · стерео',
  streaming: 'Живая запись',
  meeting: 'Встреча с ботом',
};

function whenLabel(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const p = (n) => String(n).padStart(2, '0');
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

function durationLabel(seconds) {
  if (!seconds) return '—';
  const m = Math.round(seconds / 60);
  return m >= 60 ? `${Math.floor(m / 60)} ч ${m % 60} мин` : `${m} мин`;
}


/** Адрес записи по ссылке — только http(s): в meta он пришёл из поля ввода. */
function sourceLink(meta) {
  try {
    const u = new URL(meta?.source_url || '');
    return ['http:', 'https:'].includes(u.protocol) ? u : null;
  } catch {
    return null;
  }
}

function expiresLabel(expires_at) {
  if (!expires_at) return 'бессрочно';
  const days = Math.max(0, Math.ceil((new Date(expires_at) - new Date()) / 86400000));
  return `истекает через ${days} дн.`;
}

/**
 * Протоколы записи списком. Сервис отдаёт `summaries`; ответ старого сервиса
 * (или закешированный браузером) знает только одиночный `summary` — читаем и
 * его, иначе сразу после выката страница осталась бы без протокола.
 */
export function summariesOf(item) {
  if (Array.isArray(item?.summaries) && item.summaries.length) return item.summaries;
  if (item?.summary && item.summary.text) {
    return [{ ...item.summary, id: item.summary.id || item.selected_summary_id || 'one' }];
  }
  return [];
}

/**
 * `search` — запись открыта из выдачи поиска: `{query, field, summaryId}`.
 * Тогда открывается вкладка, где нашлось (протокол `summaryId` или
 * расшифровка), вхождения подсвечены, и один раз прокручивается к первому.
 * Основным найденный протокол не становится: это решает клик человека.
 */
export default function HistoryViewer({ item, onBack, onShareChanged, onItemUpdated, onCreateProtocol, search, folderPath }) {
  const [summaries, setSummaries] = useState(() => summariesOf(item));
  const foundInSummary = search?.field === 'summary' && summaries.length > 0;
  // Основной; если его в списке нет (старый ответ, удалённый) — memo ниже
  // возьмёт последний добавленный. Из поиска — протокол, где нашлось;
  // `summaryId: null` — одиночный протокол старого формата, он же основной.
  const [currentId, setCurrentId] = useState(
    (foundInSummary && search.summaryId) || item?.selected_summary_id || null,
  );
  // Что записано основным НА СЕРВЕРЕ. Сравнивать с пропсой записи нельзя: она
  // не меняется, и возврат к первому протоколу после второго сервер бы не увидел.
  const [savedSelected, setSavedSelected] = useState(item?.selected_summary_id || null);
  const [tab, setTab] = useState(foundInSummary ? 'protocol' : 'transcript');
  const isMobile = useIsMobile();
  const [share, setShare] = useState(item?.share || null);
  const [shareOpen, setShareOpen] = useState(false);
  const [promptNames, setPromptNames] = useState({});
  // Расшифровка держится локально: после переименования участников экран
  // показывает новый текст, не дожидаясь перезагрузки записи.
  const [transcript, setTranscript] = useState(item?.transcript || '');
  const [speakersOpen, setSpeakersOpen] = useState(false);
  // Правка текста: черновик переживает переход к другой записи и перезагрузку
  // (sessionStorage в TranscriptEditor) — если он есть, открываемся сразу в правке.
  const [editing, setEditing] = useState(() => loadDraft(item?.id) !== null);
  const [findOpen, setFindOpen] = useState(false);
  const [editSaving, setEditSaving] = useState(false);
  const [editError, setEditError] = useState(null);
  const highlight = search?.query || undefined;
  // Находка по смыслу знает время места в разговоре: прокрутка к нему, а не к
  // первому совпадению слов (слов запроса в записи может и не быть).
  const focusAt = search?.at || null;
  const bodyRef = useRef(null);
  const scrolledRef = useRef(false);

  // К первому вхождению — один раз на открытие из поиска, а не на каждой
  // перерисовке: иначе переименование участников или смена вкладки дёргали бы
  // прокрутку обратно. Запись из поиска App открывает заново (key), так что
  // новый запрос по той же записи прокрутит снова. Нашлось в названии —
  // прокручивать некуда.
  useEffect(() => {
    if (scrolledRef.current || (!highlight && !focusAt) || search?.field === 'title') return;
    scrolledRef.current = true;
    const hit = (focusAt && bodyRef.current?.querySelector('[data-search-at]'))
      || bodyRef.current?.querySelector('[data-search-hit]');
    if (hit && typeof hit.scrollIntoView === 'function') hit.scrollIntoView({ block: 'center' });
  }, [highlight, focusAt, search]);

  // Имена шаблонов знает bit-auth. Не ответил — протоколы назовутся по времени,
  // список из-за этого не ломается.
  useEffect(() => {
    let alive = true;
    promptService.list()
      .then((prompts) => {
        if (!alive) return;
        // bit-auth отдаёт промпт с `_id`, не `id`: по `id` имя не находилось ни
        // разу, и все протоколы записи подписывались одинаково «Протокол · время».
        setPromptNames(Object.fromEntries((prompts || []).map((p) => [p._id, p.name])));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  const current = useMemo(
    () => summaries.find((s) => s.id === currentId) || summaries[summaries.length - 1] || null,
    [summaries, currentId],
  );
  // Что уедет по ссылке — основной на сервере, по тому же правилу, что
  // `summaries.selected()` в bit-history: по id, иначе последний добавленный.
  // Не `current`: запись, открытая из поиска на неосновном протоколе, его
  // основным не делает, и подпись в окне ссылки назвала бы не тот протокол.
  const shared = useMemo(
    () => summaries.find((s) => s.id === savedSelected) || summaries[summaries.length - 1] || null,
    [summaries, savedSelected],
  );

  // Правка протокола руками прямо в записи (обращение f63455be, 04.11.2026: «правки
  // не сохраняются, нет кнопки сохранить»). Сохраняем сами, через секунду после
  // последней буквы: кнопки «Сохранить» нет и на экране протокола. Сервер меняет
  // только основной протокол, поэтому в том же запросе правленый делается основным —
  // так же, как его делает основным выбор вкладки (choose). Шаблон и модель — те, что
  // были: правка руками не меняет того, чем протокол собран; прежний текст сервер
  // кладёт в версии протокола (source: 'manual', bit-history/app/summaries.py).
  const [protocolSave, setProtocolSave] = useState(null);   // null | 'saving' | 'saved' | {error}
  const pendingProtocolRef = useRef(null);                  // правка, ещё не ушедшая на сервер
  const protocolTimerRef = useRef(null);
  // Запросы — строго по очереди: иначе ответ на старый текст, пришедший позже,
  // оставил бы на сервере его, а не последний.
  const protocolQueueRef = useRef(Promise.resolve());
  const savedProtocolsRef = useRef(false);

  const protocolBusyRef = useRef(0);                        // запросов в очереди
  const enqueue = (fn) => {
    protocolBusyRef.current += 1;
    const run = () => Promise.resolve(fn()).finally(() => { protocolBusyRef.current -= 1; });
    protocolQueueRef.current = protocolQueueRef.current.then(run, run);
    return protocolQueueRef.current;
  };

  const flushProtocol = () => {
    clearTimeout(protocolTimerRef.current);
    protocolTimerRef.current = null;
    const edit = pendingProtocolRef.current;
    if (!edit) return protocolQueueRef.current;
    pendingProtocolRef.current = null;
    return enqueue(async () => {
      setProtocolSave('saving');
      try {
        await historyService.patch(item.id, {
          selected_summary_id: edit.id,
          summary: { template: edit.template, model: edit.model, text: edit.text, source: 'manual' },
        });
        savedProtocolsRef.current = true;
        setSavedSelected(edit.id);
        if (!pendingProtocolRef.current) setProtocolSave('saved');
      } catch (e) {
        const detail = e.response?.data?.detail;
        // Правка остаётся на экране и уйдёт со следующей буквой.
        setProtocolSave({ error: typeof detail === 'string' ? `Не сохранилось: ${detail}` : 'Не сохранилось — проверьте связь' });
      }
    });
  };
  const flushProtocolRef = useRef(flushProtocol);
  flushProtocolRef.current = flushProtocol;

  const editProtocol = (text) => {
    if (!current) return;
    setSummaries((prev) => prev.map((s) => (s.id === current.id ? { ...s, text, source: 'manual' } : s)));
    pendingProtocolRef.current = {
      id: current.id, template: current.template || '', model: current.model || '', text,
    };
    setProtocolSave(null);
    clearTimeout(protocolTimerRef.current);
    protocolTimerRef.current = setTimeout(() => flushProtocolRef.current(), 1000);
  };

  // Закрыли запись раньше, чем прошла секунда, — правка всё равно уходит. Вкладку
  // браузера с неотправленной правкой закрыть молча не даём.
  useEffect(() => {
    const guard = (e) => {
      if (!pendingProtocolRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', guard);
    return () => {
      window.removeEventListener('beforeunload', guard);
      flushProtocolRef.current();
    };
  }, []);

  // Запись в App живёт и после ухода с вкладки: без этого возврат «Назад» показал
  // бы протокол, каким он был до правки, — и правка снова выглядела бы потерянной.
  useEffect(() => {
    if (protocolSave !== 'saved') return;
    const selectedNow = summaries.find((s) => s.id === savedSelected);
    onItemUpdated?.({
      summaries,
      selected_summary_id: savedSelected,
      ...(selectedNow ? { summary: selectedNow } : {}),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [protocolSave]);

  // Три прохода по всему тексту — не на каждый рендер (смена вкладки, модалки),
  // а только при смене текста: у двухчасовой встречи это тысячи строк.
  const transcriptFacts = useMemo(() => {
    const noSpeech = resultIsEmpty(transcript);
    // Имена участников правились только в экране протокола, и из самой записи
    // до них было не добраться: узнанные по голосу подписаны, а подключившийся
    // позже третий так и оставался «Спикером 1» (обращение 1eb05751, 23.09.2026).
    const speakerLabels = noSpeech ? [] : extractSpeakerLabels(transcript);
    const hasSpeakers = speakerLabels.length > 0 && looksDiarized(transcript);
    // Кто сколько говорил (обращение 2165c419, 29.09.2026). Считается от текущего
    // текста, поэтому после переименования участников подписи меняются сразу.
    const talk = hasSpeakers ? speakerShares(transcript) : null;
    return { noSpeech, speakerLabels, hasSpeakers, talk };
  }, [transcript]);

  if (!item) return null;

  // 🔴 Запись без речи открывалась пустой панелью и ничем больше: ни строки о
  // том, что случилось. 8e4ec5c5 закрыл это на экране распознавания, но только
  // для НОВЫХ записей — уже сохранённые (у одного человека их четыре, 08.09.2026)
  // так и остались немыми. Здесь тот же признак и тот же текст.
  const { noSpeech, speakerLabels, hasSpeakers, talk } = transcriptFacts;

  // Голос отсюда не запоминается: отпечатки живут при свежей задаче
  // распознавания, у сохранённой записи их уже нет (voiceIdReady в модалке).
  const renameSpeakers = async (map) => {
    const next = applySpeakerRename(transcript, map);
    if (next === transcript) return;
    const prev = transcript;
    setTranscript(next);
    try {
      await historyService.patch(item.id, { transcript: next });
      onShareChanged?.();          // список Истории перечитает запись
    } catch (_) {
      setTranscript(prev);         // не сохранилось — показываем как есть на сервере
    }
  };

  // Правка человеком (в редакторе или «Заменить все»): сервер пишет её в журнал
  // правок с edit_kind — в отличие от переименования участников выше.
  const saveEdit = async (next, kind) => {
    if (next === transcript) return true;
    setEditSaving(true);
    setEditError(null);
    try {
      await historyService.patch(item.id, { transcript: next, edit_kind: kind });
      setTranscript(next);
      onShareChanged?.();
      return true;
    } catch (e) {
      const detail = e.response?.data?.detail;
      setEditError(typeof detail === 'string' ? detail : 'Не удалось сохранить — попробуйте ещё раз');
      return false;
    } finally {
      setEditSaving(false);
    }
  };

  const withTranscript = { ...item, transcript };

  const available = [
    ...(noSpeech ? [] : ['transcript']),
    ...(current ? ['protocol'] : []),
  ];

  // Переключение протокола делает его основным: основной открывается первым и
  // уходит по публичной ссылке, и «смотрю один, делюсь другим» — расхождение,
  // которое человеку неоткуда увидеть. Запись best-effort.
  const choose = (id) => {
    flushProtocol();             // правка прежнего протокола уходит раньше выбора
    setCurrentId(id);
    setTab('protocol');
    if (id === savedSelected) return;
    setSavedSelected(id);
    const select = () => historyService.selectSummary(item.id, id).catch(() => {});
    // Идёт сохранение правки — выбор встаёт за ним, иначе основным остался бы правленый.
    if (protocolBusyRef.current) enqueue(select); else select();
  };

  const removeCurrent = async () => {
    if (!current || !window.confirm('Удалить этот протокол? Расшифровка останется.')) return;
    if (pendingProtocolRef.current?.id === current.id) {
      clearTimeout(protocolTimerRef.current);
      pendingProtocolRef.current = null;
    }
    await flushProtocol();
    setProtocolSave(null);
    const left = summaries.filter((s) => s.id !== current.id);
    setSummaries(left);
    setCurrentId(left[left.length - 1]?.id || null);
    if (!left.length) setTab('transcript');
    try {
      await historyService.removeSummary(item.id, current.id);
      onShareChanged?.();          // список Истории мог показывать эту запись иначе
    } catch (_) {
      setSummaries(summaries);     // не удалилось — возвращаем как было
      setCurrentId(current.id);
    }
  };

  const meta = item.meta || {};
  // Распознанное по ссылке хранится тем же типом, что и файл, — отличает его
  // только адрес в meta.
  const link = sourceLink(meta);
  const sourceTitle = link ? 'Ссылка' : TYPE_TITLES[item.type];
  const facts = [
    ['Когда', whenLabel(item.created_at)],
    ['Длительность', durationLabel(meta.duration)],
    ['Источник', sourceTitle || item.type || '—'],
    ...(link ? [['Адрес', (
      <a href={link.href} target="_blank" rel="noopener noreferrer" title={link.href}
         className="underline underline-offset-[3px]">
        {link.hostname}
      </a>
    )]] : []),
    ['Протоколов', String(summaries.length)],
  ];

  // Факты о записи и ссылка. На десктопе — колонкой 292px справа, на
  // телефоне — вкладкой «О записи» (мобильный макет «Бумаги»): колонка там
  // уезжала под документ, и до неё надо было пролистать всю расшифровку.
  const aboutBlocks = (
    <>
      <div className="flex flex-col gap-[6px] rounded-panel bg-paper-card p-5 text-[13px] text-paper-ink-2">
        <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">
          Запись
        </div>
        {facts.map(([name, value]) => (
          <div key={name} className="flex justify-between gap-3">
            <span>{name}</span>
            <b className="truncate font-mono font-medium text-paper-ink">{value}</b>
          </div>
        ))}
      </div>

      {talk && (
        <div className="flex flex-col gap-[6px] rounded-panel bg-paper-card p-5 text-[13px] text-paper-ink-2">
          <div className="mb-2 font-mono text-[11px] uppercase tracking-[0.1em] text-paper-ink-3">
            Кто сколько говорил
          </div>
          {talk.shares.map(({ speaker, label }) => (
            <div key={speaker} className="flex justify-between gap-3">
              <span className="truncate" title={speaker}>{speaker}</span>
              <b className="font-mono font-medium text-paper-ink">{label}</b>
            </div>
          ))}
          <div className="mt-1 text-[12px] text-paper-ink-3">
            {talk.basis === 'time' ? 'по времени реплик' : 'по числу слов'}
          </div>
        </div>
      )}

      {share && (
        <div className="flex flex-col gap-2 rounded-panel bg-paper-card px-5 py-4 text-[13px]">
          <div className="flex items-center gap-2 font-semibold text-paper-ink">
            <span className="h-2 w-2 rounded-full bg-paper-accent" />
            Доступ по ссылке
          </div>
          <div className="text-paper-ink-2">{expiresLabel(share.expires_at)}</div>
          <div data-testid="share-views" className={share.views ? 'text-paper-ink' : 'text-paper-ink-3'}>
            {viewsLabel(share.views, share.last_view_at)}
          </div>
          <div className="flex gap-3">
            <button type="button" onClick={() => navigator.clipboard?.writeText(share.url)}
                    className="text-paper-ink underline underline-offset-[3px]">
              Копировать
            </button>
            <button type="button" onClick={() => setShareOpen(true)}
                    className="text-paper-ink-2 underline underline-offset-[3px]">
              Изменить
            </button>
          </div>
        </div>
      )}

      {/* На телефоне оба действия — липкими кнопками под документом. */}
      {!isMobile && (
      <>
      <button type="button" onClick={() => setShareOpen(true)}
              className="rounded-control bg-paper-card py-3 text-[14px] font-medium text-paper-ink hover:bg-paper-panel transition-colors">
        {share ? 'Настроить ссылку' : 'Дать ссылку'}
      </button>

      {!noSpeech && onCreateProtocol && (
        <button type="button" onClick={() => onCreateProtocol(withTranscript)}
                className="rounded-control bg-paper-accent py-[14px] text-[14px] font-semibold text-white hover:bg-paper-accent-hover transition-colors">
          {summaries.length ? 'Ещё протокол' : 'Создать протокол'}
        </button>
      )}
      </>
      )}
    </>
  );

  const TAB = (on) => `rounded-chip px-3 py-[6px] text-[13px] font-medium transition-colors ${
    on ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink hover:bg-paper-panel'}`;

  return (
    // Верстка по артборду «История · запись» (docs/redesign/2026-09-04-bumaga/):
    // документ слева карточкой, факты о записи и действия — колонкой 292px справа.
    // 🔴 minmax(0,1fr) и на узком экране: у сетки без явной колонки ширину
    // задаёт содержимое, и заголовок в одну строку выдавливал документ за край.
    <div className="grid flex-1 min-h-0 gap-3 grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_292px]">
      <div className="flex flex-col min-h-0 rounded-card bg-paper-card px-4 pb-4 pt-4 md:px-9 md:pb-5 md:pt-[30px]">
        <div className="flex shrink-0 items-start gap-3">
          <button type="button" onClick={onBack} title="К списку" aria-label="К списку"
                  className="-ml-2 -mt-1 md:m-0 md:mt-1 w-11 h-11 md:w-auto md:h-auto flex items-center justify-center shrink-0 text-paper-ink-3 hover:text-paper-ink">
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="min-w-0 flex-1">
            {/* Путь к записи (папка и «переложить»): на десктопе он в верхней строке экрана. */}
            {isMobile && folderPath && <div className="mb-1">{folderPath}</div>}
            <h1 className="m-0 truncate font-display text-[20px] md:text-[28px] font-extrabold leading-[1.1] tracking-[-0.03em] text-paper-ink" title={item.title}>
              {item.title}
            </h1>
            <div className="mt-2 text-[13px] text-paper-ink-3">
              {[whenLabel(item.created_at), durationLabel(meta.duration), sourceTitle]
                .filter((v) => v && v !== '—').join(' · ')}
            </div>
          </div>
        </div>

        <div className="mt-[18px] flex shrink-0 flex-wrap gap-1">
          <button type="button" onClick={() => setTab('transcript')} className={TAB(tab === 'transcript')}>
            Расшифровка
          </button>
          {summaries.map((s) => {
            const active = tab === 'protocol' && current?.id === s.id;
            return (
              <span key={s.id} className={`inline-flex items-center rounded-chip ${
                active ? 'bg-paper-ink text-white' : 'bg-paper-bg text-paper-ink'}`}>
                <button type="button" onClick={() => choose(s.id)}
                        className="px-3 py-[6px] text-[13px] font-medium">
                  {summaries.length > 1 ? summaryLabel(s, promptNames) : 'Протокол'}
                </button>
                {active && (
                  <button type="button" onClick={removeCurrent} title="Удалить этот протокол"
                          className="pl-[2px] pr-[7px] opacity-70 hover:opacity-100">
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </span>
            );
          })}
          {isMobile && (
            <button type="button" onClick={() => setTab('about')} className={TAB(tab === 'about')}>
              О записи
            </button>
          )}
          {/* На телефоне «+ протокол» — липкой кнопкой внизу, здесь не дублируем. */}
          {!isMobile && !noSpeech && onCreateProtocol && (
            <button type="button" onClick={() => onCreateProtocol(withTranscript)}
                    className="px-[10px] py-[6px] text-[13px] font-semibold text-paper-accent">
              {summaries.length ? '+ Ещё протокол' : '+ Создать протокол'}
            </button>
          )}
        </div>

        <div ref={bodyRef} className="mt-5 flex-1 min-h-0 overflow-y-auto md:pr-2">
          {tab === 'about' && (
            <div className="flex flex-col gap-3 [&>*]:bg-paper-panel">{aboutBlocks}</div>
          )}
          {tab === 'transcript' && noSpeech && (
            <div className="rounded-panel bg-paper-panel px-4 py-3 text-[13px] text-paper-ink-2">
              {NO_SPEECH_MESSAGE}
            </div>
          )}
          {tab === 'transcript' && !noSpeech && editing && (
            <TranscriptEditor
              itemId={item.id}
              initialText={transcript}
              saving={editSaving}
              error={editError}
              onSave={async (next) => {
                const ok = await saveEdit(next, 'manual');
                if (ok) setEditing(false);
                return ok;
              }}
              onCancel={() => { setEditError(null); setEditing(false); }}
            />
          )}
          {tab === 'transcript' && !noSpeech && !editing && (
            <>
              {findOpen && (
                <FindReplaceBar
                  text={transcript}
                  saving={editSaving}
                  onReplaceAll={(next) => saveEdit(next, 'replace_all')}
                  onClose={() => setFindOpen(false)}
                />
              )}
              {editError && <div className="mb-2 text-[13px] text-destructive">{editError}</div>}
              <TranscriptionTab
                text={transcript}
                rawText={transcript}
                sourceFileName={item.title}
                durationSec={meta.duration}
                onManualText={noop}
                onClear={undefined}
                canEditSpeakers={hasSpeakers}
                onEditSpeakers={() => setSpeakersOpen(true)}
                highlight={highlight}
                focusAt={focusAt}
                onEditText={() => { setFindOpen(false); setEditError(null); setEditing(true); }}
                onFindReplace={() => setFindOpen((v) => !v)}
              />
            </>
          )}
          {tab === 'protocol' && current && (
            <ProtocolTab
              resultText={current.text}
              isProcessing={false}
              taskStatus="completed"
              onResultChange={editProtocol}
              saveStatus={protocolSave === 'saving' ? 'Сохраняется…'
                : protocolSave === 'saved' ? 'Сохранено'
                  : protocolSave?.error || null}
              saveFailed={!!protocolSave?.error}
              onMoreDetail={undefined}
              onLessDetail={undefined}
              canMore={false}
              canLess={false}
              levelLabel=""
              sourceFileName={item.title}
              highlight={highlight}
            />
          )}
        </div>

        {/* Основное действие — липкой кнопкой над краем экрана, рядом ссылка
            (мобильный макет: «Сгенерировать протокол» + ↗). */}
        {isMobile && (
          <div className="shrink-0 pt-3 flex gap-2">
            {!noSpeech && onCreateProtocol && (
              <button type="button" onClick={() => onCreateProtocol(withTranscript)}
                      className="flex-1 h-[52px] rounded-[14px] bg-paper-accent text-[15px] font-semibold text-white">
                {summaries.length ? 'Ещё протокол' : 'Создать протокол'}
              </button>
            )}
            <button type="button" onClick={() => setShareOpen(true)} aria-label={share ? 'Настроить ссылку' : 'Дать ссылку'}
                    className={`h-[52px] rounded-[14px] bg-paper-ink text-white flex items-center justify-center gap-2 ${
                      !noSpeech && onCreateProtocol ? 'w-[52px]' : 'flex-1 text-[15px] font-semibold'}`}>
              <ArrowUpRight className="h-5 w-5" />
              {!(!noSpeech && onCreateProtocol) && (share ? 'Настроить ссылку' : 'Дать ссылку')}
            </button>
          </div>
        )}
      </div>

      {!isMobile && (
        <aside className="flex flex-col gap-3 min-h-0">
          {aboutBlocks}
        </aside>
      )}

      <EditSpeakersModal
        isOpen={speakersOpen}
        onClose={() => setSpeakersOpen(false)}
        speakers={speakerLabels}
        onSave={renameSpeakers}
      />

      {shareOpen && (
        <ShareModal
          itemId={item.id}
          title={item.title}
          available={available}
          protocolLabel={summaries.length > 1 ? summaryLabel(shared, promptNames) : null}
          share={share}
          onClose={() => setShareOpen(false)}
          onChanged={(s) => { setShare(s); onShareChanged?.(); }}
        />
      )}
    </div>
  );
}
